import {
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { Icons } from 'utoniq-ui'

import {
  StoreOrderListHistoryFragment,
  UserPaymentType,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

export type StorePurchaseHistoryProps = {
  purchaseHistory: StoreOrderListHistoryFragment
}

export const StorePurchaseHistory: Organism<StorePurchaseHistoryProps> = ({
  purchaseHistory,
}) => {
  const { t } = useTranslation('storePurchaseOrders')
  const { generatePath, routerPages } = useRouter()

  return (
    <OrganismFrame>
      <Card sx={{ borderRadius: 2 }}>
        <CardActionArea
          href={generatePath(routerPages.StorePurchaseOrderDetail, {
            purchaseOrderId: purchaseHistory.id,
          })}
        >
          <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
            <Stack direction="row" spacing={2} alignItems="center">
              {!!purchaseHistory.cancels?.length && (
                <Chip
                  color="error"
                  label={
                    <Typography variant="overline" color="white">
                      {t('refundAvailable', '返金対応')}
                    </Typography>
                  }
                  size="small"
                  sx={{ height: 18 }}
                />
              )}
              <Typography variant="body2">
                {t('orderDate', { date: purchaseHistory.formattedCreatedAt })}
              </Typography>
            </Stack>
            <Stack spacing={1} sx={{ pt: 3 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {t('orderDetails')}
              </Typography>
              {purchaseHistory.items.map((item, index) => {
                if (item.skus.length) {
                  return item.skus.map(
                    sku =>
                      sku && (
                        <Stack
                          key={sku.skuId}
                          spacing={2}
                          direction="row"
                          justifyContent="space-between"
                        >
                          <Stack flex={1} sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={styleSets.lineCamp(1)}
                            >
                              {item.item.title} ({sku.sku.unitName})
                            </Typography>
                          </Stack>
                          <Typography variant="subtitle1">
                            {t('quantity', { quantity: sku.count })}
                          </Typography>
                        </Stack>
                      )
                  )
                }
                return (
                  <Stack
                    spacing={2}
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Stack flex={1} sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        sx={styleSets.lineCamp(1)}
                      >
                        {item.item.title}
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle1">
                      {t('quantity', { quantity: item.count })}
                    </Typography>
                  </Stack>
                )
              })}
            </Stack>
            <Stack spacing={1} sx={{ pt: 3 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {purchaseHistory.paymentType === UserPaymentType.Cash
                  ? t('payment')
                  : t('totalPoint')}
              </Typography>
              {purchaseHistory.paymentType === UserPaymentType.Cash ? (
                <Typography variant="subtitle1">
                  ￥{purchaseHistory.formattedTotalAmount}
                </Typography>
              ) : (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Icons.Point fontSize="small" />
                  <Typography variant="subtitle1">
                    {purchaseHistory.formattedTotalAmount}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </OrganismFrame>
  )
}
